import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { T } from "@/lib/testIds";
import { ArrowLeft, Printer, Award, ShieldCheck, Copy, FileDown } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function Certificate() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [course, setCourse] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      const results = await Promise.allSettled([
        api.get(`/courses/${slug}`),
        api.get(`/progress/${slug}`),
        api.get("/practice/settings"),
      ]);
      if (results[0].status === "fulfilled") setCourse(results[0].value.data);
      if (results[1].status === "fulfilled") setProgress(results[1].value.data);
      if (results[2].status === "fulfilled") setSettings(results[2].value.data);
    })();
  }, [slug]);

  if (!course || !progress || !settings) return <Layout><div className="text-brand-muted">Loading…</div></Layout>;
  if (!progress.quiz_passed) {
    return (
      <Layout>
        <div className="max-w-xl">
          <h1 className="font-serif text-4xl text-brand-ink">Not yet</h1>
          <p className="mt-3 text-brand-body">You need to pass the final assessment before your course certificate is issued.</p>
          <Link to={`/courses/${slug}/quiz`} className="inline-flex items-center gap-2 mt-6 bg-brand-ink text-brand-bone rounded-full px-6 py-3 hover:bg-brand-inkHover">
            Take the assessment
          </Link>
        </div>
      </Layout>
    );
  }

  const issued = fmtDate(progress.completed_at);
  const expires = progress.certificate_expires_at ? fmtDate(progress.certificate_expires_at) : null;
  const isExpired = progress.certificate_expires_at ? new Date(progress.certificate_expires_at) < new Date() : false;
  const verifyPath = progress.certificate_id ? `/verify/${progress.certificate_id}` : null;
  const verifyUrl = verifyPath ? `${window.location.origin}${verifyPath}` : null;

  const copyLink = () => {
    if (!verifyUrl) return;
    navigator.clipboard.writeText(verifyUrl);
    toast.success("Verify link copied");
  };

  const handlePrint = () => {
    // Wait for the verification QR image to be fully loaded so it survives print.
    const qr = document.querySelector('[data-testid="certificate-qr"]');
    if (qr && !qr.complete) {
      qr.addEventListener("load", () => window.print(), { once: true });
      qr.addEventListener("error", () => window.print(), { once: true });
      // Safety net so a broken external service can't block printing forever
      setTimeout(() => window.print(), 1500);
      return;
    }
    window.print();
  };

  const downloadPdf = async () => {
    const node = document.getElementById("certificate");
    if (!node) return;
    // Force the certificate to render at A4-landscape proportions during capture
    // (297 × 210 mm ≈ 1123 × 794 px at 96dpi). We temporarily widen the node so
    // the html2canvas snapshot fills an A4 sheet cleanly.
    const original = {
      width: node.style.width,
      maxWidth: node.style.maxWidth,
      minHeight: node.style.minHeight,
    };
    node.style.width = "1123px";
    node.style.maxWidth = "1123px";
    node.style.minHeight = "794px";
    try {
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: "#F7F1E8",
        useCORS: true,
        windowWidth: 1200,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.9);
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
      const pageW = pdf.internal.pageSize.getWidth();   // 297
      const pageH = pdf.internal.pageSize.getHeight();  // 210
      // Fit-to-page while preserving aspect ratio
      const ratio = Math.min(pageW / canvas.width, pageH / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      const x = (pageW - w) / 2;
      const y = (pageH - h) / 2;
      pdf.addImage(imgData, "JPEG", x, y, w, h);
      const filename = `${user?.name?.replace(/\s+/g, "_") || "certificate"}_${course.slug}.pdf`;
      pdf.save(filename);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed — try Print instead");
    } finally {
      // Restore the on-screen certificate to its normal responsive size
      node.style.width = original.width;
      node.style.maxWidth = original.maxWidth;
      node.style.minHeight = original.minHeight;
    }
  };

  return (
    <Layout wide>
      <div className="flex items-center justify-between mb-6 print:hidden gap-3 flex-wrap">
        <Link to={`/courses/${slug}`} className="inline-flex items-center gap-2 text-sm text-brand-secondary hover:text-brand-ink">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to course
        </Link>
        <div className="flex items-center gap-2">
          {verifyUrl && (
            <button onClick={copyLink} data-testid="certificate-copy-verify"
              className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-5 py-2.5 hover:bg-brand-linen">
              <Copy className="w-4 h-4" strokeWidth={1.5} /> Copy verify link
            </button>
          )}
          <button data-testid="certificate-download-pdf" onClick={downloadPdf}
            className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover">
            <FileDown className="w-4 h-4" strokeWidth={1.5} /> Download PDF
          </button>
          <button data-testid={T.certificatePrint} onClick={handlePrint}
            className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-5 py-2.5 hover:bg-brand-linen">
            <Printer className="w-4 h-4" strokeWidth={1.5} /> Print
          </button>
        </div>
      </div>

      <div id="certificate" className="bg-brand-bone border-[6px] border-double border-brand-ink rounded-3xl p-8 sm:p-14 shadow-card relative overflow-hidden mx-auto max-w-[297mm] print:max-w-none print:rounded-none print:border-2 print:shadow-none">
        {/* Practice branding */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt={`${settings.practice_name} logo`}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="h-14 w-14 sm:h-16 sm:w-16 object-contain rounded-lg bg-white border border-brand-linen p-1 flex-shrink-0" />
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.28em] text-brand-muted">Issued by</div>
              <div className="font-serif text-2xl sm:text-3xl text-brand-ink leading-tight break-words pb-1" data-testid="certificate-practice-name">{settings.practice_name}</div>
            </div>
          </div>
          {isExpired ? (
            <span className="text-[11px] uppercase tracking-widest px-3 py-1 rounded-full bg-brand-accent text-white whitespace-nowrap flex-shrink-0">Expired</span>
          ) : (
            <span className="text-[11px] uppercase tracking-widest px-3 py-1 rounded-full bg-brand-ink text-brand-bone inline-flex items-center gap-1 whitespace-nowrap flex-shrink-0">
              <ShieldCheck className="w-3 h-3" strokeWidth={2} /> Valid
            </span>
          )}
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.32em] text-brand-muted">
            <Award className="w-4 h-4" strokeWidth={1.5} /> Calside · Course Certificate
          </div>
          <h1 className="font-serif text-4xl sm:text-6xl text-brand-ink mt-6 leading-none">Course Certificate</h1>
          <div className="mt-4 text-brand-body">This is to certify that</div>
          <div className="font-serif text-3xl sm:text-4xl text-brand-accent italic mt-3">{user?.name}</div>
          <div className="mt-3 text-brand-body max-w-2xl mx-auto leading-relaxed">
            has successfully completed the course
          </div>
          <div className="font-serif text-2xl sm:text-3xl text-brand-ink mt-2">{course.title}</div>
          <div className="mt-2 text-brand-body">with a final assessment score of <strong>{progress.quiz_score}%</strong>.</div>

          <div className="grid sm:grid-cols-3 gap-6 max-w-2xl mx-auto mt-12 text-left">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-muted">Issued</div>
              <div className="font-serif text-xl text-brand-ink mt-1">{issued}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-muted">Valid until</div>
              <div className={`font-serif text-xl mt-1 ${isExpired ? "text-brand-accent" : "text-brand-ink"}`}>{expires || "No expiry"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-muted">Signed</div>
              <div className="font-serif text-xl text-brand-ink mt-1 italic">{settings.signatory_name}</div>
              <div className="text-xs text-brand-muted">{settings.signatory_title}</div>
            </div>
          </div>

          {verifyUrl && (
            <div className="mt-12 pt-6 border-t border-brand-linen flex flex-col sm:flex-row items-center justify-between gap-4 text-left">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-brand-muted">Certificate ID</div>
                <div className="font-mono text-xs sm:text-sm text-brand-secondary break-all mt-1">{progress.certificate_id}</div>
                <div className="text-[11px] text-brand-muted mt-2">Verify at <span className="underline">{verifyUrl}</span></div>
                {settings.regulator && (
                  <div className="text-[11px] text-brand-muted mt-2 italic">
                    Suitable evidence for CPD records and training portfolios.
                  </div>
                )}
                {!settings.regulator && (
                  <div className="text-[11px] text-brand-muted mt-2 italic">
                    Suitable evidence for CPD records and training portfolios.
                  </div>
                )}
                {course?.governance && (
                  <div className="text-[10px] text-brand-muted mt-3 leading-relaxed" data-testid="certificate-governance-line">
                    Content reviewed by {course.governance.reviewed_by || "—"}
                    {course.governance.reviewed_at ? ` on ${fmtDate(course.governance.reviewed_at)}` : ""}
                    {course.governance.next_review_due ? ` · next review due ${fmtDate(course.governance.next_review_due)}` : ""}.
                  </div>
                )}
              </div>
              <img
                alt="Verification QR"
                data-testid="certificate-qr"
                crossOrigin="anonymous"
                className="h-24 w-24 rounded-lg bg-white border border-brand-linen p-1 flex-shrink-0"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=4&data=${encodeURIComponent(verifyUrl)}`}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
